import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SecondApi } from "../api/secondApi";
import { getSecondApiLocale } from "../helper/apiLocale";
import OtpInput from "./OtpInput";
import PasswordField from "./PasswordField";

const CODE_LENGTH = 4;
const RESEND_INTERVAL_MS = 30 * 1000;

function pickErrorMessage(data, fallback) {
  if (!data) return fallback;
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }
  if (data.errors && typeof data.errors === "object") {
    for (const v of Object.values(data.errors)) {
      const arr = Array.isArray(v) ? v : [v];
      const first = arr.find((x) => typeof x === "string" && x.trim());
      if (first) return first;
    }
  }
  return fallback;
}

function ForgotPasswordModal({ onSuccess, onSwitchToLogin }) {
  const { t, i18n } = useTranslation();
  // 1 = enter email, 2 = enter code, 3 = enter new password
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [resendUntilMs, setResendUntilMs] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [resending, setResending] = useState(false);
  const [message, setMessage] = useState(null);

  // 30s countdown for resend button on step 2
  useEffect(() => {
    if (!resendUntilMs) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((resendUntilMs - Date.now()) / 1000));
      setSecondsLeft(remaining);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [resendUntilMs]);

  useEffect(() => {
    if (!message) return;
    if (message.status) return;
    const t1 = setTimeout(() => setMessage(null), 5000);
    return () => clearTimeout(t1);
  }, [message]);

  const startResendCooldown = useCallback(() => {
    setResendUntilMs(Date.now() + RESEND_INTERVAL_MS);
  }, []);

  // ---- Step 1: request reset code ----
  const emailSchema = Yup.object({
    email: Yup.string()
      .email(t("authModal.login.errors.emailInvalid"))
      .required(t("authModal.login.errors.emailRequired")),
  });

  const requestReset = async (targetEmail, { setSubmitting } = {}) => {
    const locale = getSecondApiLocale(i18n.language);
    setMessage(null);
    try {
      const { data } = await SecondApi.post(
        `/api/${locale}/auth/resetPassword`,
        { email: targetEmail },
      );
      const okText =
        data && typeof data.message === "string" && data.message.trim()
          ? data.message
          : t("authModal.forgot.codeSent");
      setMessage({ status: true, text: okText });
      setEmail(targetEmail);
      setStep(2);
      startResendCooldown();
    } catch (error) {
      const fallback =
        error.code === "ERR_NETWORK" || !error.response
          ? t("authModal.login.errors.network")
          : t("authModal.forgot.errors.requestGeneric");
      setMessage({
        status: false,
        text: pickErrorMessage(error.response?.data, fallback),
      });
    } finally {
      setSubmitting?.(false);
    }
  };

  const handleStep1 = (values, helpers) => requestReset(values.email.trim(), helpers);

  const handleResend = async () => {
    if (secondsLeft > 0 || resending) return;
    setResending(true);
    await requestReset(email);
    setResending(false);
  };

  // ---- Step 2: verify reset code ----
  const handleVerifyCode = async (e) => {
    e?.preventDefault?.();
    if (verificationCode.length !== CODE_LENGTH) {
      setMessage({
        status: false,
        text: t("authModal.verify.errors.incomplete"),
      });
      return;
    }
    const locale = getSecondApiLocale(i18n.language);
    setMessage(null);
    try {
      const { data } = await SecondApi.post(
        `/api/${locale}/auth/verifyResetPassword`,
        { email, verification_code: verificationCode },
      );
      const ok =
        data?.isSuccess === true ||
        data?.success === true ||
        (typeof data?.message === "string" &&
          /verified|success|ok/i.test(data.message));
      if (ok) {
        setMessage(null);
        setStep(3);
      } else {
        setMessage({
          status: false,
          text: pickErrorMessage(data, t("authModal.verify.errors.generic")),
        });
      }
    } catch (error) {
      const fallback =
        error.code === "ERR_NETWORK" || !error.response
          ? t("authModal.verify.errors.network")
          : t("authModal.verify.errors.generic");
      setMessage({
        status: false,
        text: pickErrorMessage(error.response?.data, fallback),
      });
    }
  };

  // ---- Step 3: set new password ----
  const passwordSchema = Yup.object({
    password: Yup.string()
      .min(8, t("authModal.register.errors.passwordMin"))
      .required(t("authModal.register.errors.passwordRequired")),
    password_confirmation: Yup.string()
      .oneOf(
        [Yup.ref("password")],
        t("authModal.register.errors.passwordMismatch"),
      )
      .required(t("authModal.register.errors.passwordConfirmRequired")),
  });

  const handleStep3 = async (values, { setSubmitting }) => {
    const locale = getSecondApiLocale(i18n.language);
    setMessage(null);
    try {
      const { data } = await SecondApi.post(
        `/api/${locale}/auth/changeResetPassword`,
        {
          verification_code: verificationCode,
          email,
          password: values.password,
          password_confirmation: values.password_confirmation,
        },
      );
      const ok =
        data?.isSuccess === true ||
        data?.success === true ||
        (typeof data?.message === "string" &&
          /success|updated|changed/i.test(data.message));
      if (ok) {
        setMessage({
          status: true,
          text:
            (data && typeof data.message === "string" && data.message.trim()) ||
            t("authModal.forgot.success"),
        });
        if (typeof onSuccess === "function") {
          setTimeout(() => onSuccess(email), 800);
        }
      } else {
        setMessage({
          status: false,
          text: pickErrorMessage(data, t("authModal.forgot.errors.changeGeneric")),
        });
      }
    } catch (error) {
      const fallback =
        error.code === "ERR_NETWORK" || !error.response
          ? t("authModal.login.errors.network")
          : t("authModal.forgot.errors.changeGeneric");
      setMessage({
        status: false,
        text: pickErrorMessage(error.response?.data, fallback),
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ---- Render ----
  if (step === 1) {
    return (
      <Formik
        initialValues={{ email: "" }}
        validationSchema={emailSchema}
        onSubmit={handleStep1}
      >
        {({ isSubmitting }) => (
          <Form className="row gy-4">
            <div className="col-12">
              <p className="font-14 mb-0 text-body">
                {t("authModal.forgot.step1.intro")}
              </p>
            </div>

            {message && (
              <div
                className={`col-12 font-14 ${
                  message.status ? "text-success" : "text-danger"
                }`}
                role="status"
              >
                {message.text}
              </div>
            )}

            <div className="col-12">
              <label className="form-label mb-2 font-18 font-heading fw-600">
                {t("authModal.login.labels.email")}
              </label>
              <div className="position-relative">
                <Field
                  type="email"
                  name="email"
                  autoComplete="email"
                  className="common-input common-input--withIcon"
                  placeholder={t("authModal.login.placeholders.email")}
                />
                <span className="input-icon">
                  <img src="../assets/images/icons/envelope-icon.svg" alt="" />
                </span>
              </div>
              <ErrorMessage
                name="email"
                component="div"
                className="text-danger mt-1 font-14"
              />
            </div>

            <div className="col-12">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-main btn-lg w-100 pill"
              >
                {isSubmitting
                  ? t("authModal.forgot.step1.btnLoading")
                  : t("authModal.forgot.step1.btn")}
              </button>
            </div>

            {typeof onSwitchToLogin === "function" && (
              <div className="col-12">
                <p className="text font-14 mb-0 text-center">
                  {t("authModal.forgot.rememberPassword")}{" "}
                  <button
                    type="button"
                    className="link text-main text-decoration-underline fw-500 bg-transparent border-0 p-0"
                    onClick={onSwitchToLogin}
                  >
                    {t("authModal.forgot.goLogin")}
                  </button>
                </p>
              </div>
            )}
          </Form>
        )}
      </Formik>
    );
  }

  if (step === 2) {
    const canResend = secondsLeft === 0 && !resending;
    return (
      <form onSubmit={handleVerifyCode} className="row gy-4">
        <div className="col-12">
          <button
            type="button"
            onClick={() => {
              setStep(1);
              setVerificationCode("");
              setMessage(null);
            }}
            className="link text-main fw-500 bg-transparent border-0 p-0 font-14 d-inline-flex align-items-center gap-1"
          >
            <i className="las la-arrow-left" />
            {t("authModal.forgot.step2.changeEmail")}
          </button>
        </div>

        <div className="col-12">
          <p className="font-14 mb-0 text-body">
            {t("authModal.forgot.step2.intro", { email })}
          </p>
        </div>

        {message && (
          <div
            className={`col-12 font-14 ${
              message.status ? "text-success" : "text-danger"
            }`}
            role="status"
          >
            {message.text}
          </div>
        )}

        <div className="col-12">
          <label className="form-label mb-2 font-18 font-heading fw-600">
            {t("authModal.verify.codeLabel")}
          </label>
          <OtpInput
            length={CODE_LENGTH}
            value={verificationCode}
            onChange={setVerificationCode}
          />
        </div>

        <div className="col-12">
          <button
            type="submit"
            disabled={verificationCode.length !== CODE_LENGTH}
            className="btn btn-main btn-lg w-100 pill"
          >
            {t("authModal.forgot.step2.btn")}
          </button>
        </div>

        <div className="col-12 mb-0">
          <p className="text font-14 mb-0 text-center">
            {canResend ? (
              <button
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="link text-main text-decoration-underline fw-500 bg-transparent border-0 p-0 font-14"
              >
                {resending
                  ? t("authModal.verify.resending")
                  : t("authModal.verify.resendBtn")}
              </button>
            ) : (
              <span className="text-body">
                {t("authModal.verify.resendIn", { seconds: secondsLeft })}
              </span>
            )}
          </p>
        </div>
      </form>
    );
  }

  // step === 3
  return (
    <Formik
      initialValues={{ password: "", password_confirmation: "" }}
      validationSchema={passwordSchema}
      onSubmit={handleStep3}
    >
      {({ isSubmitting }) => (
        <Form className="row gy-4">
          <div className="col-12">
            <p className="font-14 mb-0 text-body">
              {t("authModal.forgot.step3.intro")}
            </p>
          </div>

          {message && (
            <div
              className={`col-12 font-14 ${
                message.status ? "text-success" : "text-danger"
              }`}
              role="status"
            >
              {message.text}
            </div>
          )}

          <div className="col-12">
            <label className="form-label mb-2 font-18 font-heading fw-600">
              {t("authModal.forgot.step3.passwordLabel")}
            </label>
            <PasswordField
              name="password"
              autoComplete="new-password"
            />
            <ErrorMessage
              name="password"
              component="div"
              className="text-danger mt-1 font-14"
            />
          </div>

          <div className="col-12">
            <label className="form-label mb-2 font-18 font-heading fw-600">
              {t("authModal.forgot.step3.passwordConfirmLabel")}
            </label>
            <PasswordField
              name="password_confirmation"
              autoComplete="new-password"
            />
            <ErrorMessage
              name="password_confirmation"
              component="div"
              className="text-danger mt-1 font-14"
            />
          </div>

          <div className="col-12">
            <button
              type="submit"
              disabled={isSubmitting}
              className="btn btn-main btn-lg w-100 pill"
            >
              {isSubmitting
                ? t("authModal.forgot.step3.btnLoading")
                : t("authModal.forgot.step3.btn")}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export default ForgotPasswordModal;
